// Main entry point for Unite and Slay client
import { SceneManager } from './managers/SceneManager.js';
import { UIManager } from './managers/UIManager.js';
import { AssetManager } from './managers/AssetManager.js';
import { RenderLoop } from './core/RenderLoop.js';
import { Game } from './Game.js';
import { BARBARIAN, KNIGHT, MAGE, ROGUE } from './controllers/Player.js';
import Logger from './utils/Logger.js';

// Initialize silent logging to suppress console messages
Logger.init(true);

// Initialize the game
let game;
let uiManager;

// Create and initialize UI Manager
document.addEventListener('DOMContentLoaded', () => {
    // DOM fully loaded
    
    // Initialize UI Manager
    uiManager = new UIManager();
    if (!uiManager.init()) {
        return;
    }
    
    // Set global reference immediately
    window.uiManager = uiManager;
    
    // Setup login form event listeners
    setupLoginEvents();
    
    // Create game instance
    game = new Game();
    
    // Expose game instance to global window for debugging and the test button
    window.game = game;
    
    // Set callbacks for UI interactions
    uiManager.setCallbacks({
        onLogin: handleLogin,
        onCharacterSelect: handleCharacterSelect,
        onInventoryChange: handleInventoryChange
    });
    
    // Start loading assets - this will initialize the game after loading
    startGame();
});

/**
 * Setup login-related event listeners
 */
function setupLoginEvents() {
    // Guest login button
    const enterGameBtn = document.getElementById('enter-game-btn');
    if (enterGameBtn) {
        enterGameBtn.addEventListener('click', () => {
            uiManager.handleLogin(true);
        });
    }
    
    // Toggle account login form
    const accountBtn = document.getElementById('account-btn');
    if (accountBtn) {
        accountBtn.addEventListener('click', () => {
            const accountLogin = document.getElementById('account-login');
            accountLogin.classList.toggle('hidden');
        });
    }
    
    // Account login button
    const loginBtn = document.getElementById('login-btn');
    if (loginBtn) {
        loginBtn.addEventListener('click', () => {
            uiManager.handleLogin(false);
        });
    }
    
    // Enter key in input fields
    const guestName = document.getElementById('guest-name');
    if (guestName) {
        guestName.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                uiManager.handleLogin(true);
            }
        });
    }
    
    const username = document.getElementById('username');
    const password = document.getElementById('password');
    
    if (username && password) {
        const handleEnterPress = (e) => {
            if (e.key === 'Enter') {
                uiManager.handleLogin(false);
            }
        };
        
        username.addEventListener('keypress', handleEnterPress);
        password.addEventListener('keypress', handleEnterPress);
    }
}

/**
 * Handle login event
 * @param {string} username - Username entered by player
 * @param {boolean} isGuest - Whether this is a guest login
 */
function handleLogin(username, isGuest) {
    // Store player data
    game.playerData = {
        username,
        isGuest
    };
    
    // In a real game, we would authenticate with the server here
    // For now, we just proceed to character selection
}

/**
 * Handle character selection event
 * @param {string} characterType - Type of character selected ('barbarian' or 'swat')
 */
function handleCharacterSelect(characterType) {
    // Ensure game is initialized
    if (!game || !game.sceneManager || !game.assetManager) {
        return;
    }
    
    // Create the appropriate player character
    let player;
    
    switch (characterType) {
        case 'barbarian':
            // For Barbarian, pass the scene reference and asset manager
            player = new BARBARIAN(null, game.assetManager, {
                name: game.playerData.username
            });
            break;
        case 'knight':
            // For KNIGHT, pass the scene reference and asset manager
            player = new KNIGHT(null, game.assetManager, {
                name: game.playerData.username
            });
            break;
        case 'mage':
            // For MAGE, pass the scene reference and asset manager
            player = new MAGE(null, game.assetManager, {
                name: game.playerData.username
            });
            break;
        case 'rogue':
            // For ROGUE, pass the scene reference and asset manager
            player = new ROGUE(null, game.assetManager, {
                name: game.playerData.username
            });
            break;
        default:
            return;
    }
    
    // Show game UI elements
    uiManager.showGameUI();
    
    // Complete character setup and initialize the game world
    completeSetup(player);
}

/**
 * Handle inventory slot change event
 * @param {number} slotIndex - Selected inventory slot index
 */
function handleInventoryChange(slotIndex) {
    if (game.player) {
        game.player.activeSlot = slotIndex;
    }
}

/**
 * Setup game controls
 */
function setupGameControls() {
    // Keyboard controls
    document.addEventListener('keydown', (e) => {
        if (!game.player) return;
        
        switch (e.key) {
            case 'w':
            case 'ArrowUp':
                game.player.moveDirection = { forward: 1, right: 0 };
                break;
            case 's':
            case 'ArrowDown':
                game.player.moveDirection = { forward: -1, right: 0 };
                break;
            case 'a':
            case 'ArrowLeft':
                game.player.moveDirection = { forward: 0, right: -1 };
                break;
            case 'd':
            case 'ArrowRight':
                game.player.moveDirection = { forward: 0, right: 1 };
                break;
            case ' ':
                // Attack
                game.player.attack();
                break;
            case '1':
            case '2':
            case '3':
            case '4':
                // Change inventory slot
                const slotIndex = parseInt(e.key) - 1;
                uiManager.selectInventorySlot(slotIndex);
                break;
        }
    });
    
    document.addEventListener('keyup', (e) => {
        if (!game.player) return;
        
        switch (e.key) {
            case 'w':
            case 's':
            case 'ArrowUp':
            case 'ArrowDown':
                game.player.moveDirection.forward = 0;
                break;
            case 'a':
            case 'd':
            case 'ArrowLeft':
            case 'ArrowRight':
                game.player.moveDirection.right = 0;
                break;
        }
    });
    
    // Mouse controls for rotation
    document.addEventListener('mousemove', (e) => {
        if (!game.player) return;
        
        // Get canvas-relative coordinates
        const canvas = document.getElementById('game-canvas');
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Pass to game for player rotation
        game.handleMouseMove(mouseX, mouseY);
    });
    
    // Mouse click for attacks
    document.addEventListener('mousedown', (e) => {
        if (!game.player || e.button !== 0) return; // Left click only
        
        game.player.attack();
    });
}

/**
 * Start the game and begin loading assets
 */
function startGame() {
    // Initialize the game
    if (!game.init()) {
        console.error('Failed to initialize game');
        return;
    }
    
    // Show login screen first instead of loading screen
    uiManager.showScreen('login');
    
    // Start loading assets on the login screen
    game.loadAssets((progress, message) => {
        // Update loading progress in UI
        uiManager.updateLoadingProgress(progress, message);
        
        // When loading is complete, preload character selection assets
        if (progress >= 100) {
            preloadCharacterAssets();
            
            // Enable the login button once assets are loaded
            const enterGameBtn = document.getElementById('enter-game-btn');
            if (enterGameBtn) {
                enterGameBtn.disabled = false;
                enterGameBtn.textContent = "Enter Game";
            }
        }
    });
}

/**
 * Preload assets needed for character selection
 */
function preloadCharacterAssets() {
    // In a real game, we would preload character models here
    
    // Simulate loading character previews
    const barbarianPreview = new Image();
    barbarianPreview.src = '/assets/models/characters/barbarian.png';
    
    const magePreview = new Image();
    magePreview.src = '/assets/models/characters/mage.png';

    const knightPreview = new Image();
    knightPreview.src = '/assets/models/characters/knight.png';

    const roguePreview = new Image();
    roguePreview.src = '/assets/models/characters/rogue.png';
}

/**
 * Complete character setup and initialize the game world
 * @param {Player} player - Selected player character
 */
function completeSetup(player) {
    // Set player character
    if (!game.setPlayer(player)) {
        return;
    }
    
    // Initialize game world
    const worldInitSuccess = game.initWorld();
    
    if (!worldInitSuccess) {
        return;
    }
    
    // Hide the UI container with status and test controls
    const uiContainer = document.getElementById('ui-container');
    if (uiContainer) {
        uiContainer.classList.add('hidden');
    }
    
    // Add event listeners for player controls
    setupGameControls();
} 